// src/state/auth/context.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authApi from '../../api/auth';
import { setAuthToken } from '../../utils/authToken';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
 const [isAuthenticated, setIsAuthenticated] = useState(false);
 const [isLoading, setIsLoading] = useState(true);
 const [user, setUser] = useState(null);
 const navigate = useNavigate();

 // Enhanced authentication check with better session persistence
 useEffect(() => {
   const checkAuthStatus = async () => {
     try {
       setIsLoading(true);

       const token = authApi.getAuthToken();
       if (!token) {
         setIsAuthenticated(false);
         setUser(null);
         setIsLoading(false);
         return;
       }

       // First, try to decode the token locally to check basic validity
       try {
         const parts = token.split('.');
         if (parts.length === 3) {
           const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
           const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
             return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
           }).join(''));
           const payload = JSON.parse(jsonPayload);

           // Check if token has expired locally first
           if (payload.exp && payload.exp * 1000 <= Date.now()) {
             authApi.removeAuthToken();
             setIsAuthenticated(false);
             setUser(null);
             setIsLoading(false);
             return;
           }

           // Set user info from token
           setUser({ email: payload.email });
         }
       } catch (tokenDecodeError) {
         console.warn('Could not decode token locally:', tokenDecodeError);
       }

       // Then validate with server
       try {
         const isValid = await authApi.checkSession();

         setIsAuthenticated(isValid);

         if (!isValid) {
           authApi.removeAuthToken();
           setUser(null);
           // Only redirect if we're not already on the login page
           if (window.location.pathname !== '/') {
             navigate('/', { replace: true });
           }
         }
       } catch (sessionError) {
         // If the session check endpoint fails, we'll assume the token is valid
         // This prevents API errors from logging users out unnecessarily
         setIsAuthenticated(true);
       }
     } catch (error) {
       console.error('Auth status check failed:', error);
       setIsAuthenticated(false);
       setUser(null);
     } finally {
       setIsLoading(false);
     }
   };

   checkAuthStatus();

   // Set up periodic session validation (every 30 minutes)
   const sessionCheckInterval = setInterval(() => {
     const token = authApi.getAuthToken();
     if (token && isAuthenticated) {
       // Only check if the page is visible to avoid unnecessary API calls
       if (!document.hidden) {
         authApi.checkSession().then(isValid => {
           if (!isValid) {
             authApi.removeAuthToken();
             setIsAuthenticated(false);
             setUser(null);
             if (window.location.pathname !== '/') {
               navigate('/', { replace: true });
             }
           }
         }).catch(error => {
           console.warn('Periodic session check error:', error);
           // Don't log out on network errors during periodic checks
         });
       }
     }
   }, 30 * 60 * 1000); // 30 minutes

   // Check session when page becomes visible again
   const handleVisibilityChange = () => {
     if (!document.hidden && isAuthenticated) {
       const token = authApi.getAuthToken();
       if (token) {
         authApi.checkSession().then(isValid => {
           if (!isValid) {
             authApi.removeAuthToken();
             setIsAuthenticated(false);
             setUser(null);
             if (window.location.pathname !== '/') {
               navigate('/', { replace: true });
             }
           }
         }).catch(() => {
           // Session check error on visibility change
         });
       }
     }
   };

   document.addEventListener('visibilitychange', handleVisibilityChange);

   // Cleanup interval and event listener on unmount
   return () => {
     clearInterval(sessionCheckInterval);
     document.removeEventListener('visibilitychange', handleVisibilityChange);
   };
 }, [navigate, isAuthenticated]);

 const login = async (token) => {
   try {
     setAuthToken(token);
     setIsAuthenticated(true);

     // Extract user info from token
     try {
       const parts = token.split('.');
       if (parts.length === 3) {
         const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
         const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
           return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
         }).join(''));
         const payload = JSON.parse(jsonPayload);
         setUser({ email: payload.email });
       }
     } catch (tokenDecodeError) {
       console.warn('Could not decode token during login:', tokenDecodeError);
     }

     // Navigate to dashboard page after successful login
     navigate('/dashboard', { replace: true });
   } catch (error) {
     setIsAuthenticated(false);
     setUser(null);
     throw error;
   }
 };

 const logout = async () => {
   try {
     await authApi.logout();
     // Navigate back to login page after logout
     navigate('/', { replace: true });
   } catch (error) {
     // Logout error
   } finally {
     setIsAuthenticated(false);
     setUser(null);
   }
 };

 // Expose checkAuthStatus as a method that can be called manually when needed
 const manualCheckAuthStatus = async () => {
   try {
     setIsLoading(true);

     const token = authApi.getAuthToken();
     if (!token) {
       setIsAuthenticated(false);
       setUser(null);
       return false;
     }

     try {
       const isValid = await authApi.checkSession();
       setIsAuthenticated(isValid);

       if (!isValid) {
         authApi.removeAuthToken();
         setUser(null);
         // Only redirect if we're not already on the login page
         if (window.location.pathname !== '/') {
           navigate('/', { replace: true });
         }
       }

       return isValid;
     } catch (sessionError) {
       // If the session check endpoint fails, we'll assume the token is valid
       console.warn('Manual session check endpoint failed, assuming token is valid:', sessionError);
       setIsAuthenticated(true);
       return true;
     }
   } catch (error) {
     console.error('Manual auth status check failed:', error);
     setIsAuthenticated(false);
     setUser(null);
     if (window.location.pathname !== '/') {
       navigate('/', { replace: true });
     }
     return false;
   } finally {
     setIsLoading(false);
   }
 };

 return (
   <AuthContext.Provider
     value={{
       isAuthenticated,
       isLoading,
       user,
       login,
       logout,
       checkAuthStatus: manualCheckAuthStatus
     }}
   >
     {children}
   </AuthContext.Provider>
 );
};

export const useAuth = () => {
 const context = useContext(AuthContext);
 if (!context) {
   throw new Error('useAuth must be used within an AuthProvider');
 }
 return context;
};

export default AuthContext;